"use client"

import { useState, useMemo, Suspense, useRef, useEffect, useCallback, useId } from "react"
import dynamic from "next/dynamic"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import Link from "next/link"
import {
  Sun,
  Leaf,
  ArrowRight,
  Wand2,
  Shield,
  Zap,
  ChevronDown,
  Check,
} from "lucide-react"
import { GrassTuft } from "@/components/icons/grass-tuft"
import { LawnPrefillBanner, ToolDataChartSkeleton, RelatedTools, ToolPageLoadingFallback, ResultEmailCaptureCard } from "@/components/tools/shared"

const GrassComparisonDataChart = dynamic(
  () => import("@/components/tools/charts/grass-comparison-data-chart").then((m) => m.GrassComparisonDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={400} /> }
)
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { cn } from "@/lib/utils"
import { getComparisonSeedPicks } from "@/lib/grass-comparison/product-picks"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useFromTool } from "@/hooks/use-from-tool"
import { getRegionFromZipCode } from "@/data/regional-climate"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { getGrassComparisonByKeys, type GrassKey } from "@/data/comparisons"

import {
  toleranceToNumber,
  slugToGrassName,
  parseGrassesParam,
  parseZipParam,
} from '@/lib/grass-comparison-helpers'
import { computeTotals } from "@/lib/grass-comparison-scoring"

import { VerdictCard } from "@/components/tools/grass-comparison/verdict-card"
import { ComparisonSeedPanel } from "@/components/tools/grass-comparison/comparison-seed-panel"
import { ComparisonTable } from "@/components/tools/grass-comparison/comparison-table"
import { ProsConsCards } from "@/components/tools/grass-comparison/pros-cons-cards"
import { PickerBar } from "@/components/tools/grass-comparison/picker-bar"
import { ZipField } from "@/components/tools/grass-comparison/zip-field"
import { HowWeScoreDisclosure } from "@/components/tools/grass-comparison/how-we-score-disclosure"
import { useDefaultLawn } from "@/hooks/use-default-lawn"

// Re-exported so existing imports of these helpers from this module continue
// to work; canonical definitions live in `lib/grass-comparison-helpers.ts`.
export { toleranceToNumber, slugToGrassName, parseGrassesParam }

// Get all grass type names
const GRASS_TYPES = Object.keys(GRASS_CHARACTERISTICS)

// Grass selector dropdown (kept inline — the picker bar wraps these and we
// preserve the existing keyboard nav / cool+warm grouping rather than
// extracting and re-importing).
interface GrassSelectorProps {
  label: string
  value: string
  onChange: (value: string) => void
  excludeValues: string[]
}

function GrassSelector({ label, value, onChange, excludeValues }: GrassSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listboxId = `grass-selector-${useId().replace(/:/g, '')}`

  const availableGrasses = useMemo(
    () => GRASS_TYPES.filter(g => !excludeValues.includes(g) || g === value),
    [excludeValues, value]
  )

  const coolSeasonGrasses = useMemo(
    () => availableGrasses.filter(g => GRASS_CHARACTERISTICS[g]?.season === 'Cool'),
    [availableGrasses]
  )
  const warmSeasonGrasses = useMemo(
    () => availableGrasses.filter(g => GRASS_CHARACTERISTICS[g]?.season === 'Warm'),
    [availableGrasses]
  )

  const allGrasses = useMemo(
    () => [...coolSeasonGrasses, ...warmSeasonGrasses],
    [coolSeasonGrasses, warmSeasonGrasses]
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      const currentIndex = allGrasses.indexOf(value)
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0)
    }
  }, [isOpen, value, allGrasses])

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-grass-item]')
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, isOpen])

  const handleSelect = (grass: string) => {
    onChange(grass)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < allGrasses.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : allGrasses.length - 1
        )
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (highlightedIndex >= 0 && allGrasses[highlightedIndex]) {
          handleSelect(allGrasses[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
      case 'Tab':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  return (
    <div className="relative min-w-[180px] flex-1 basis-[220px]" ref={dropdownRef}>
      <button
        type="button"
        role="combobox"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && highlightedIndex >= 0
            ? `${listboxId}-option-${highlightedIndex}`
            : undefined
        }
        aria-label={`${label}: ${value || "Select a grass type"}`}
        className={cn(
          "w-full min-h-[44px] px-4 py-2.5 rounded-xl border bg-white shadow-xs text-left flex items-center justify-between transition-all duration-200",
          isOpen
            ? "border-green-500 ring-2 ring-green-500/20"
            : "border-gray-200 hover:border-green-300 hover:shadow-sm"
        )}
      >
        <span className={cn("text-sm font-medium truncate", value ? "text-gray-900" : "text-gray-500")}>
          {value || "Select a grass type..."}
        </span>
        <ChevronDown className={cn(
          "w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <div
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label={`${label} options`}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-[280px] overflow-y-auto"
        >
          {coolSeasonGrasses.length > 0 && (
            <div className="p-1">
              <div className="px-3 py-2 text-xs font-medium text-gray-500">
                ❄️ Cool Season
              </div>
              {coolSeasonGrasses.map(grass => {
                const index = allGrasses.indexOf(grass)
                return (
                  <button
                    key={grass}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={grass === value}
                    data-grass-item
                    onClick={() => handleSelect(grass)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "w-full px-3 py-2 text-sm text-left rounded-lg flex items-center justify-between transition-colors",
                      grass === value && "bg-green-50 text-green-700",
                      highlightedIndex === index && grass !== value && "bg-gray-100",
                      grass !== value && highlightedIndex !== index && "text-gray-900"
                    )}
                  >
                    {grass}
                    {grass === value && <Check className="w-4 h-4 text-green-600" />}
                  </button>
                )
              })}
            </div>
          )}
          {warmSeasonGrasses.length > 0 && (
            <div className="p-1 border-t border-gray-100">
              <div className="px-3 py-2 text-xs font-medium text-gray-500">
                ☀️ Warm Season
              </div>
              {warmSeasonGrasses.map(grass => {
                const index = allGrasses.indexOf(grass)
                return (
                  <button
                    key={grass}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={grass === value}
                    data-grass-item
                    onClick={() => handleSelect(grass)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "w-full px-3 py-2 text-sm text-left rounded-lg flex items-center justify-between transition-colors",
                      grass === value && "bg-green-50 text-green-700",
                      highlightedIndex === index && grass !== value && "bg-gray-100",
                      grass !== value && highlightedIndex !== index && "text-gray-900"
                    )}
                  >
                    {grass}
                    {grass === value && <Check className="w-4 h-4 text-green-600" />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function GrassComparisonContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("grass-comparison")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const sunlight = searchParams.get('sunlight') ?? lawnPrefill.sunlight
  const editedPrefillRef = useRef(false)

  // Seed initial state from ?grasses=foo,bar URL param when present.
  const initialGrasses = useMemo(() => {
    const raw = searchParams.get('grasses')
    const matched = parseGrassesParam(raw)
    if (matched.length < 2) return ['Kentucky Bluegrass', 'Buffalograss']
    return matched
  }, [searchParams])

  const initialZip = useMemo(() => parseZipParam(searchParams.get('zip')) ?? '', [searchParams])

  const [selectedGrasses, setSelectedGrasses] = useState<string[]>(initialGrasses)
  const [zip, setZip] = useState<string>(initialZip)
  // The tool renders a default comparison on mount, so calculator_used must
  // wait for a real interaction (grass change or ZIP entry). Arriving with a
  // ?grasses= param is itself intent, so that case tracks immediately.
  const interactedRef = useRef(false)
  const arrivedWithGrassesParam = useMemo(
    () => parseGrassesParam(searchParams.get('grasses')).length >= 2,
    [searchParams],
  )

  useEffect(() => {
    if (editedPrefillRef.current) return
    if (!initialZip && lawnPrefill.zipCode && !zip) setZip(lawnPrefill.zipCode)
    if (!searchParams.get('grasses') && lawnPrefill.grassType && !selectedGrasses.includes(lawnPrefill.grassType)) {
      setSelectedGrasses((prev) => [lawnPrefill.grassType!, ...prev.slice(0, 2)].slice(0, 3))
    }
  }, [initialZip, lawnPrefill, searchParams, selectedGrasses, zip])

  const updateGrass = (index: number, value: string) => {
    editedPrefillRef.current = true
    interactedRef.current = true
    const newGrasses = [...selectedGrasses]
    newGrasses[index] = value
    setSelectedGrasses(newGrasses)
    trackUsage('grass-comparison')
  }

  const addGrass = useCallback(() => {
    interactedRef.current = true
    setSelectedGrasses((prev) => {
      if (prev.length >= 3) return prev
      const available = GRASS_TYPES.find((g) => !prev.includes(g))
      return available ? [...prev, available] : prev
    })
  }, [])

  const removeGrass = useCallback((index: number) => {
    interactedRef.current = true
    setSelectedGrasses((prev) => (prev.length > 2 ? prev.filter((_, i) => i !== index) : prev))
  }, [])

  const swapGrasses = useCallback(() => {
    interactedRef.current = true
    setSelectedGrasses((prev) => {
      if (prev.length < 2) return prev
      return [prev[1], prev[0], ...prev.slice(2)]
    })
  }, [])

  // Get grass data for selected types
  const grassData = useMemo(() => {
    return selectedGrasses
      .filter(g => g && GRASS_CHARACTERISTICS[g])
      .map(g => ({ ...GRASS_CHARACTERISTICS[g] }))
  }, [selectedGrasses])

  const validSelection = grassData.length >= 2

  // Resolve region once from the typed ZIP so the verdict, ZipField chip, and
  // the verdict card eyebrow share the same source of truth.
  const region = useMemo(() => {
    if (!/^\d{5}$/.test(zip)) return null
    const r = getRegionFromZipCode(zip)
    return r === 'Unknown' ? null : r
  }, [zip])

  const totals = useMemo(
    () => computeTotals(grassData, region ? zip : null, { sunlight }),
    [grassData, zip, region, sunlight],
  )

  const championName =
    validSelection && !totals.tied && totals.winnerIdx >= 0
      ? grassData[totals.winnerIdx].name
      : null

  // When the user has narrowed to exactly two grasses that have a dedicated
  // /compare/[slug] article, surface a link to it. The static article is the
  // page type that ranks for "X vs Y" queries, so this funnels the interactive
  // tool into it (and adds an internal link). Skip for 3-way picks, which have
  // no single pairwise page.
  const matchedComparison = useMemo(() => {
    if (selectedGrasses.length !== 2) return null
    return getGrassComparisonByKeys(selectedGrasses[0] as GrassKey, selectedGrasses[1] as GrassKey)
  }, [selectedGrasses])

  // Enthusiast-grade seed to buy for each selected grass (+ one starter). Curated
  // per-grass so the affiliate picks are reliable, not keyword-scored guesses.
  const comparisonSeedPicks = useMemo(
    () => getComparisonSeedPicks(selectedGrasses, championName),
    [selectedGrasses, championName]
  )

  // Keep the URL in sync with picks + ZIP. ?grasses=... stays back-compat;
  // ?zip=... only writes when we have a resolved region.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const params = new URLSearchParams(window.location.search)
      if (selectedGrasses.length >= 2) {
        params.set(
          'grasses',
          selectedGrasses
            .map((g) => g.toLowerCase().replace(/\s+/g, '-'))
            .join(','),
        )
      } else {
        params.delete('grasses')
      }
      if (region) {
        params.set('zip', zip)
      } else {
        params.delete('zip')
      }
      const next = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState(null, '', next)
    } catch {
      // History API not available — silent fall-through is fine.
    }
  }, [selectedGrasses, zip, region])

  // Fire calculator_used once per unique (grasses, zip) combination. The
  // original tool keyed on grasses only — including zip lets PostHog show
  // when users actually engaged ZIP scoring. Never fires for the default
  // comparison rendered on mount: it needs a real interaction, or a
  // ?grasses= arrival (which is intent in itself).
  const lastTrackedKey = useRef<string | null>(null)
  useEffect(() => {
    if (!validSelection) return
    if (!interactedRef.current && !arrivedWithGrassesParam) return
    const key = `${grassData.map((g) => g.name).sort().join('|')}::${region ?? ''}`
    if (lastTrackedKey.current === key) return
    lastTrackedKey.current = key
    analytics.calculatorUsed('grass-comparison', {
      inputs: {
        selected_grasses: grassData.map((g) => g.name),
        comparison_count: grassData.length,
        zip_region: region ?? undefined,
      },
      source: interactedRef.current ? 'live_update' : 'url_params',
    })
  }, [grassData, validSelection, region, arrivedWithGrassesParam])

  // Quick Insights (kept) — uses tied detection. Recomputed independently of
  // the verdict so the cards still render even when ZIP isn't supplied.
  const insights = useMemo(() => {
    if (!validSelection) return null

    const pickByScore = <T,>(
      items: T[],
      score: (item: T) => number,
      mode: "max" | "min"
    ): { winner: T; tied: boolean } => {
      const scores = items.map(score)
      const target = mode === "max" ? Math.max(...scores) : Math.min(...scores)
      const winnerIdx = scores.indexOf(target)
      const winners = scores.filter((s) => s === target).length
      return { winner: items[winnerIdx], tied: winners > 1 }
    }

    const mostDroughtTolerant = pickByScore(
      grassData,
      (g) => toleranceToNumber(g.droughtTolerance),
      "max"
    )

    const bestForShade = pickByScore(
      grassData,
      (g) => toleranceToNumber(g.shadeTolerance),
      "max"
    )

    const maintenanceOrder = ["Very Low", "Low", "Medium", "Medium-High", "High"]
    const lowestMaintenance = pickByScore(
      grassData,
      (g) => maintenanceOrder.indexOf(g.maintenanceLevel || "High"),
      "min"
    )

    const getFastestDays = (time: string | undefined) => {
      if (!time) return 999
      const match = time.match(/(\d+)/)
      return match ? parseInt(match[1]) : 999
    }
    const fastestEstablish = pickByScore(
      grassData,
      (g) => getFastestDays(g.establishmentTime),
      "min"
    )

    return {
      mostDroughtTolerant,
      bestForShade,
      lowestMaintenance,
      fastestEstablish,
    }
  }, [grassData, validSelection])

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${buildToolUrl('grass-comparison', {
          grasses: selectedGrasses,
          zip: region ? zip : undefined,
          sunlight,
        })}`
      : ''

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Grass Comparison" }
          ]}
        />

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Hero */}
          <section className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0 flex-1">
              <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-green-700">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-700">
                  <Leaf className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                Grass comparison
              </p>
              <h1 className="mt-3 max-w-xl text-balance text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
                Which grass{' '}
                <span className="border-b-[3px] border-green-600 pb-0.5 text-green-600">
                  wins for your yard?
                </span>
              </h1>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-gray-700 sm:text-lg">
                Pick two or three grasses and we score them head-to-head across six traits. Add a ZIP and we&apos;ll factor in your climate zone.
              </p>
              <p className="mt-3 max-w-xl text-sm text-gray-600">
                Want a written deep-dive on a specific matchup?{' '}
                <Link
                  href="/compare"
                  className="font-medium text-green-700 underline-offset-2 hover:underline"
                >
                  Browse our grass-vs-grass guides
                </Link>
                .
              </p>
            </div>
            <ZipField
              value={zip}
              onChange={(next) => {
                editedPrefillRef.current = true
                interactedRef.current = true
                setZip(next)
              }}
              className="md:max-w-xs"
            />
          </section>

          <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />

          {/* Picker bar — sticky */}
          <PickerBar
            selectedGrasses={selectedGrasses}
            championName={championName}
            onSwap={swapGrasses}
            onRemove={removeGrass}
            onAddThird={addGrass}
          >
            {selectedGrasses.map((grass, index) => (
              <GrassSelector
                key={index}
                label={`Grass ${index + 1}`}
                value={grass}
                onChange={(value) => updateGrass(index, value)}
                excludeValues={selectedGrasses.filter((_, i) => i !== index)}
              />
            ))}
          </PickerBar>

          {/* Comparison Results */}
          {validSelection && (
            <>
              {/* Verdict + Share row */}
              <VerdictCard grasses={grassData} totals={totals} region={region} />

              {/* Post-verdict peak: the seed to buy for each grass, up top where
                  it's seen — not buried at the bottom. Curated per-grass so the
                  picks are reliable; empty when no selected grass is seed-viable.
                  Copy names only the seeded grasses (a St. Augustine + Bermuda
                  comparison shows only Bermuda seed) and notes the sod/plugs ones. */}
              {!totals.noClimateCompatibleWinner && (
                <ComparisonSeedPanel seedPicks={comparisonSeedPicks} />
              )}

              <ComparisonTable grasses={grassData} />

              <ResultEmailCaptureCard
                source="tool_result_grass_comparison"
                toolDisplayName="grass comparison"
                subcopy="Seasonal care tips for the grass you choose."
                grassType={championName}
                zipCode={region ? zip : null}
                accountLead={{
                  redirectTo: buildToolUrl("grass-comparison", {
                    grasses: selectedGrasses,
                    zip: region ? zip : undefined,
                    sunlight,
                  }),
                  headline: "Save my grass comparison",
                  subcopy: "Keep this grass comparison in a free account and track your lawn over time.",
                  // Grass-aware (grassType + zipCode passed) so the subscribe route
                  // sends the 12-month calendar. Name it, per .claude/rules/tools-pages.md.
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_grass_comparison"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />

              {matchedComparison && (
                <Link
                  href={`/compare/${matchedComparison.slug}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-green-200 bg-green-50/60 px-5 py-4 transition-colors hover:bg-green-50"
                >
                  <span className="text-sm sm:text-base text-gray-800">
                    Want the written deep-dive?{' '}
                    <span className="font-semibold text-green-800">
                      Read the full {matchedComparison.shortLabel} guide
                    </span>{' '}
                    for the climate breakdown, pick-one decision blocks, and FAQs.
                  </span>
                  <ArrowRight
                    className="h-5 w-5 shrink-0 text-green-700 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </Link>
              )}

              <ProsConsCards grasses={grassData} />

              {/* Share + methodology */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
                <UnifiedShareButton
                  url={shareUrl}
                  title="Grass Type Comparison"
                  text={`Compare ${selectedGrasses.join(' vs ')}`}
                />
                <HowWeScoreDisclosure />
              </div>

              {/* Source line */}
              <p className="text-xs leading-relaxed text-gray-500">
                Trait ratings synthesized from university extension data (UGA, NC State, UF/IFAS, Penn State, PSU, Clemson, UMN). Last reviewed Feb 2026.
              </p>

              {/* Quick Insights */}
              {insights && (
                <Card className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Wand2 className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Quick Insights</h3>
                  </div>
                  {/* Each trait-winner tile gets its OWN accent so the four
                      champions are scannable at a glance (recognition over
                      recall) instead of a uniform gray wall. Class strings are
                      literal — Tailwind v4 JIT won't compile interpolated ones. */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 [container:insights/inline-size]">
                    {[
                      { label: "Most Drought Tolerant", Icon: Sun, iconWrap: "bg-amber-50", iconColor: "text-amber-600", data: insights.mostDroughtTolerant },
                      { label: "Best for Shade", Icon: Leaf, iconWrap: "bg-green-50", iconColor: "text-green-600", data: insights.bestForShade },
                      { label: "Lowest Maintenance", Icon: Shield, iconWrap: "bg-sky-50", iconColor: "text-sky-600", data: insights.lowestMaintenance },
                      { label: "Fastest to Establish", Icon: Zap, iconWrap: "bg-violet-50", iconColor: "text-violet-600", data: insights.fastestEstablish },
                    ].map(({ label, Icon, iconWrap, iconColor, data }) => (
                      <div key={label} className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className={`p-1.5 sm:p-2 rounded-lg ${iconWrap}`}>
                          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="insight-card-label text-xs sm:text-sm text-gray-500 font-medium">{label}</p>
                          <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">{data.tied ? "Tied" : data.winner.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <GrassComparisonDataChart selectedGrasses={selectedGrasses} />
            </>
          )}

          {/* CTA Section */}
          <div className="text-center p-8 bg-linear-to-br from-green-600 to-emerald-600 rounded-2xl">
            <h3 className="text-2xl font-bold text-white mb-2">
              Not sure what grass you have?
            </h3>
            <p className="text-green-50 mb-6">
              Upload a photo and we&apos;ll identify your grass type instantly.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-green-600 hover:bg-green-50 min-h-[44px]"
            >
              <Link href="/">
                <GrassTuft className="w-5 h-5 mr-2" />
                Identify My Grass
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>

          <RelatedTools
            currentToolId="grass-comparison"
            prefillValues={{
              zip: zip || lawnPrefill.zipCode,
              grassType: totals.noClimateCompatibleWinner
                ? undefined
                : championName ?? selectedGrasses[0] ?? lawnPrefill.grassType,
              grasses: selectedGrasses,
              lawnSize: lawnPrefill.lawnSizeSqft,
              sunlight,
            }}
          />

        </div>
      </Reveal>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Grass Comparison"
        usageCount={usageCount}
        grassType={championName}
        zipCode={region ? zip : null}
      />
    </div>
  )
}

export function GrassComparisonPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="grass-comparison" />}>
      <SearchParamsHydrationGate>
        <GrassComparisonContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
